import { useController, useFormContext } from "react-hook-form";
import { Box, Chip, FormControl, FormHelperText } from "@mui/material";
import { ReactMultiEmail } from "react-multi-email";
import "react-multi-email/dist/style.css";
import PropTypes from "prop-types";
import { colorsFoundation } from "@accredo/fe-shared/theme/index.js";

/**
 * A multi-email input component integrated with react-hook-form.
 * Uses react-multi-email for entering multiple email addresses.
 *
 * @param {string} name - The name of the field in the form.
 * @param {string} label - The placeholder/label for the field.
 * @param {string} helperText - Optional helper text to display below the field.
 * @param {boolean} disabled - Whether the field is disabled.
 * @returns {JSX.Element}
 */
const FormEmailInput = ({
  name,
  label,
  helperText,
  disabled,
  ...otherProps
}) => {
  const { control } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: [],
    shouldUnregister: false,
  });

  if (disabled) {
    return (
      <Box
        sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}
      >
        {label}:
        {(field.value || []).map((email, index) => (
          <Chip key={index} label={email} />
        ))}
      </Box>
    );
  }

  return (
    <FormControl error={!!error} style={{ width: "100%" }}>
      <ReactMultiEmail
        placeholder={label}
        emails={field.value || []}
        onChange={(emails) => {
          field.onChange([...emails]);
        }}
        onBlur={field.onBlur}
        getLabel={(email, index, removeEmail) => (
          <div data-tag key={index}>
            <div data-tag-item>{email}</div>
            <span data-tag-handle onClick={() => removeEmail(index)}>
              ×
            </span>
          </div>
        )}
        {...otherProps}
      />
      <FormHelperText>{error?.message || helperText || ""}</FormHelperText>
    </FormControl>
  );
};

FormEmailInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
};

export default FormEmailInput;
